import { useEffect, useState } from 'react';
import type { Prefs } from '../lib/settings';
import { logout, type Me } from '../lib/account';
import { tones } from '../lib/tones';
import { ChevronIcon, CloseIcon } from './Icons';

interface Props {
  prefs: Prefs;
  me: Me;
  onSave: (p: Prefs) => void;
  onCancel: () => void;
}

function useDevices() {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    const refresh = () => navigator.mediaDevices?.enumerateDevices().then(setDevices).catch(() => undefined);
    refresh();
    navigator.mediaDevices?.addEventListener('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener('devicechange', refresh);
  }, []);
  return devices;
}

const canPickOutput = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

function DeviceRow(props: { label: string; value: string; devices: MediaDeviceInfo[]; onChange: (v: string) => void }) {
  return (
    <li className="row plain form-row">
      <span>{props.label}</span>
      <select value={props.value} onChange={(e) => props.onChange(e.target.value)} aria-label={props.label}>
        <option value="">System default</option>
        {props.devices
          .filter((d) => d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications')
          .map((d) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || props.label}
            </option>
          ))}
      </select>
      <ChevronIcon className="chev" />
    </li>
  );
}

export function SettingsView({ prefs, me, onSave, onCancel }: Props) {
  const [p, setP] = useState(prefs);
  const devices = useDevices();
  const set = <K extends keyof Prefs>(k: K, v: Prefs[K]) => setP((prev) => ({ ...prev, [k]: v }));
  const inputs = devices.filter((d) => d.kind === 'audioinput');
  const outputs = devices.filter((d) => d.kind === 'audiooutput');

  return (
    <form
      className="settings"
      data-testid="settings"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(p);
      }}
    >
      <div className="page-head">
        <h2 className="large-title">Settings</h2>
        <button type="button" className="close-btn" aria-label="Close settings" data-testid="settings-close" onClick={onCancel}>
          <CloseIcon />
        </button>
      </div>

      <h3 className="section-title">Your line</h3>
      <ul className="group">
        <li className="row plain form-row">
          <span>Name</span>
          <span className="value">{me.user.name}</span>
        </li>
        <li className="row plain form-row">
          <span>Extension</span>
          <span className="value">{me.line.extension}</span>
        </li>
        <li className="row plain form-row">
          <span>Caller ID</span>
          <span className="value">{me.line.callerIdDisplay || '—'}</span>
        </li>
        <li className="row plain form-row">
          <span>Signed in as</span>
          <span className="value">{me.user.email}</span>
        </li>
      </ul>

      <h3 className="section-title">Audio</h3>
      <ul className="group">
        <DeviceRow label="Microphone" value={p.inputDeviceId} devices={inputs} onChange={(v) => set('inputDeviceId', v)} />
        {canPickOutput && (
          <>
            <DeviceRow label="Speaker" value={p.outputDeviceId} devices={outputs} onChange={(v) => set('outputDeviceId', v)} />
            <DeviceRow label="Ringer" value={p.ringDeviceId} devices={outputs} onChange={(v) => set('ringDeviceId', v)} />
            <li className="row plain form-row">
              <button
                type="button"
                className="link"
                onClick={() => {
                  tones.setSinkId(p.ringDeviceId);
                  tones.play('ringtone');
                  window.setTimeout(() => tones.stop('ringtone'), 2500);
                }}
              >
                Test ringer
              </button>
            </li>
          </>
        )}
      </ul>
      {!canPickOutput && <p className="section-foot">This browser plays calls through the system output — change it in your device's sound settings.</p>}

      <h3 className="section-title">Diagnostics</h3>
      <ul className="group">
        <li className="row plain form-row">
          <span>SIP trace in console</span>
          <input type="checkbox" checked={p.sipTrace} onChange={(e) => set('sipTrace', e.target.checked)} aria-label="SIP trace in console" />
        </li>
      </ul>
      <p className="section-foot">Takes effect after reloading the page.</p>

      <div className="row-buttons">
        <button type="submit" className="btn primary">
          Save
        </button>
      </div>

      <ul className="group" style={{ marginTop: 24 }}>
        <li>
          <button type="button" className="row plain danger" onClick={logout}>
            Sign Out
          </button>
        </li>
      </ul>
    </form>
  );
}
